import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, getToken } from '../api';
import { formatConstitution } from '../lib/constitutionLabels';
import { compressImage } from '../components/ProfilePrompt';
import LocationSearch from '../components/LocationSearch';

function ScorePill({ label, score }) {
  if (score == null) return null;
  const color = score >= 70 ? '#c9a84c' : score >= 45 ? '#666' : '#3a3a3a';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <div style={{ fontSize: 10, color: 'var(--very-muted)', letterSpacing: 1, textTransform: 'uppercase' }}>{label}</div>
      <div style={{ fontSize: 13, color, fontFamily: 'var(--font-display)', fontWeight: 300 }}>{score}</div>
    </div>
  );
}

const textareaStyle = {
  width: '100%',
  background: 'rgba(255,255,255,0.04)',
  border: '0.5px solid rgba(255,255,255,0.1)',
  color: '#fff',
  fontFamily: 'var(--font-ui)',
  fontSize: 13,
  fontWeight: 300,
  padding: '10px 12px',
  resize: 'none',
  outline: 'none',
  lineHeight: 1.55,
  boxSizing: 'border-box',
  borderRadius: 0,
};

const inputStyle = {
  ...textareaStyle,
  padding: '9px 12px',
};

function PartnerCard({ partner, onDelete, onUpdate }) {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();

  const [name, setName] = useState(partner.name || '');
  const [whatWorked, setWhatWorked] = useState(partner.what_worked || '');
  const [challenges, setChallenges] = useState(partner.challenges || '');
  const [birthDay, setBirthDay] = useState(partner.birth_day ? String(partner.birth_day) : '');
  const [birthMonth, setBirthMonth] = useState(partner.birth_month ? String(partner.birth_month) : '');
  const [birthYear, setBirthYear] = useState(partner.birth_year ? String(partner.birth_year) : '');
  const [photoBase64, setPhotoBase64] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [birthHour, setBirthHour] = useState(partner.birth_hour ? String(partner.birth_hour) : '');
  const [birthMin, setBirthMin] = useState(partner.birth_min ? String(partner.birth_min) : '');
  const [birthLocation, setBirthLocation] = useState(
    partner.birth_lat ? { lat: partner.birth_lat, lng: partner.birth_lon, tzone: partner.birth_tzone } : null
  );
  const [deeper, setDeeper] = useState(partner.deeper_summary || null);
  const [deeperLoading, setDeeperLoading] = useState(false);

  const handlePhoto = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPhotoPreview(URL.createObjectURL(file));
    try {
      const base64 = await compressImage(file);
      setPhotoBase64(base64);
    } catch {
      const reader = new FileReader();
      reader.onload = ev => setPhotoBase64(ev.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleDelete = async (e) => {
    e.stopPropagation();
    setDeleting(true);
    try {
      await api.delete(`/partner-tests/${partner.id}`);
      onDelete(partner.id);
    } catch {
      setDeleting(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        name: name.trim() || null,
        what_worked: whatWorked.trim() || null,
        challenges: challenges.trim() || null,
        birth_day: birthDay ? parseInt(birthDay) : null,
        birth_month: birthMonth ? parseInt(birthMonth) : null,
        birth_year: birthYear ? parseInt(birthYear) : null,
        birth_hour: birthHour ? parseInt(birthHour) : null,
        birth_min: birthMin ? parseInt(birthMin) : null,
        birth_lat: birthLocation?.lat ?? null,
        birth_lon: birthLocation?.lng ?? null,
        birth_tzone: birthLocation?.tzone ?? null,
      };
      if (photoBase64) payload.photo = photoBase64;
      const updated = await api.patch(`/partner-tests/${partner.id}`, payload);
      onUpdate(partner.id, updated);
      setEditing(false);
      setPhotoBase64(null);
      setPhotoPreview(null);
      setDeeper(null); // reset so it re-fetches with new data
    } catch {
      // silently fail, keep form open
    } finally {
      setSaving(false);
    }
  };

  const handleDeeper = async () => {
    setDeeperLoading(true);
    try {
      const data = await api.get(`/partner-tests/${partner.id}/deeper`);
      setDeeper(data.summary);
    } catch (err) {
      setDeeper(err.message || 'Could not load deeper read.');
    } finally {
      setDeeperLoading(false);
    }
  };

  const cancelEdit = () => {
    setEditing(false);
    setName(partner.name || '');
    setWhatWorked(partner.what_worked || '');
    setChallenges(partner.challenges || '');
    setBirthDay(partner.birth_day ? String(partner.birth_day) : '');
    setBirthMonth(partner.birth_month ? String(partner.birth_month) : '');
    setBirthYear(partner.birth_year ? String(partner.birth_year) : '');
    setBirthHour(partner.birth_hour ? String(partner.birth_hour) : '');
    setBirthMin(partner.birth_min ? String(partner.birth_min) : '');
    setBirthLocation(partner.birth_lat ? { lat: partner.birth_lat, lng: partner.birth_lon, tzone: partner.birth_tzone } : null);
    setPhotoBase64(null);
    setPhotoPreview(null);
  };

  const displayName = partner.name || 'Unnamed';
  const birthDisplay = partner.birth_day && partner.birth_month && partner.birth_year
    ? `${partner.birth_month}/${partner.birth_day}/${partner.birth_year}`
    : partner.birth_year ? `b. ${partner.birth_year}` : null;

  return (
    <div style={{
      background: '#0f0f0f',
      border: '1px solid #1e1e1e',
      overflow: 'hidden',
    }}>
      {/* Header row — always visible */}
      <div
        onClick={() => { setExpanded(e => !e); if (!expanded) setEditing(false); }}
        style={{ display: 'flex', gap: 14, padding: 16, alignItems: 'flex-start', cursor: 'pointer' }}
      >
        <div style={{
          width: 60, height: 80, flexShrink: 0,
          overflow: 'hidden', background: '#1a1a1a',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {partner.photo_url ? (
            <img src={partner.photo_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : (
            <span style={{ fontSize: 22, color: '#333' }}>?</span>
          )}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 400, marginBottom: 2 }}>
            {displayName}
            {birthDisplay && <span style={{ fontSize: 12, color: 'var(--very-muted)', marginLeft: 6 }}>{birthDisplay}</span>}
          </div>
          {partner.partner_dosha && (
            <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 8 }}>
              {formatConstitution(partner.partner_dosha)}
            </div>
          )}
          <div style={{ display: 'flex', gap: 16 }}>
            <ScorePill label="Attract" score={partner.attraction_score} />
            <ScorePill label="Bio" score={partner.bio_harmonics} />
            <ScorePill label="Chrono" score={partner.chrono_dynamics} />
          </div>
          {(partner.what_worked || partner.challenges) && (
            <div style={{ marginTop: 6, fontSize: 11, color: 'var(--very-muted)', letterSpacing: 0.5 }}>
              Has relationship notes
            </div>
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
          <button
            onClick={handleDelete}
            disabled={deleting}
            style={{ background: 'none', border: 'none', color: '#444', fontSize: 18, cursor: 'pointer', padding: '0 4px', lineHeight: 1 }}
          >
            {deleting ? '…' : '×'}
          </button>
          <span style={{ fontSize: 11, color: 'var(--very-muted)' }}>{expanded ? '▲' : '▼'}</span>
        </div>
      </div>

      {/* Expanded section */}
      {expanded && (
        <div style={{ borderTop: '1px solid #1a1a1a', padding: '16px 16px 20px' }}>
          {!editing ? (
            <>
              {/* Read mode */}
              {partner.summary && (
                <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.7, fontWeight: 300, marginBottom: 16 }}>
                  {partner.summary}
                </div>
              )}

              {partner.what_worked && (
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 10, color: 'var(--very-muted)', letterSpacing: 1, marginBottom: 4 }}>WHAT WORKED</div>
                  <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.6)', lineHeight: 1.6, fontWeight: 300 }}>
                    {partner.what_worked}
                  </div>
                </div>
              )}

              {partner.challenges && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 10, color: 'var(--very-muted)', letterSpacing: 1, marginBottom: 4 }}>CHALLENGES</div>
                  <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.6)', lineHeight: 1.6, fontWeight: 300 }}>
                    {partner.challenges}
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <button
                  onClick={(e) => { e.stopPropagation(); setEditing(true); }}
                  style={{
                    background: 'none', border: '0.5px solid rgba(255,255,255,0.1)',
                    color: 'var(--very-muted)', fontSize: 12, padding: '8px 16px',
                    cursor: 'pointer', letterSpacing: 0.5,
                  }}
                >
                  Edit
                </button>
                {partner.has_vedic_breakdown && (
                  <button
                    onClick={(e) => { e.stopPropagation(); if (!deeper) handleDeeper(); }}
                    disabled={deeperLoading}
                    style={{
                      background: deeper ? 'none' : 'rgba(201,168,76,0.06)',
                      border: '0.5px solid rgba(201,168,76,0.3)',
                      color: deeper ? 'var(--very-muted)' : 'var(--gold)',
                      fontSize: 12, padding: '8px 16px',
                      cursor: deeperLoading ? 'default' : 'pointer', letterSpacing: 0.5,
                    }}
                  >
                    {deeperLoading ? 'Reading…' : deeper ? 'Deep read ✓' : 'Go deeper →'}
                  </button>
                )}
              </div>

              {deeper && (
                <div style={{
                  marginTop: 16, padding: '14px 16px',
                  background: 'rgba(201,168,76,0.04)',
                  border: '0.5px solid rgba(201,168,76,0.15)',
                  fontSize: 13, color: 'rgba(255,255,255,0.55)',
                  lineHeight: 1.75, fontWeight: 300,
                }}>
                  <div style={{ fontSize: 10, color: 'rgba(201,168,76,0.5)', letterSpacing: 1, marginBottom: 8 }}>DEEPER READ</div>
                  {deeper}
                </div>
              )}
            </>
          ) : (
            <>
              {/* Edit mode */}

              {/* Photo */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 10, color: 'var(--very-muted)', letterSpacing: 1, marginBottom: 8 }}>PHOTO</div>
                <div
                  onClick={() => fileRef.current?.click()}
                  style={{
                    width: 80, height: 100, border: '0.5px solid rgba(255,255,255,0.12)',
                    background: '#111', cursor: 'pointer', overflow: 'hidden',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative',
                  }}
                >
                  {photoPreview || partner.photo_url ? (
                    <img src={photoPreview || partner.photo_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <span style={{ fontSize: 20, color: '#333' }}>+</span>
                  )}
                  <div style={{
                    position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.35)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 10, color: 'rgba(255,255,255,0.5)', letterSpacing: 0.5,
                  }}>
                    change
                  </div>
                </div>
                <input ref={fileRef} type="file" accept="image/*" onChange={handlePhoto} style={{ display: 'none' }} />
                {photoBase64 && (
                  <div style={{ fontSize: 10, color: 'rgba(201,168,76,0.6)', marginTop: 6 }}>
                    New photo — face scan will re-run on save
                  </div>
                )}
              </div>

              {/* Name */}
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 10, color: 'var(--very-muted)', letterSpacing: 1, marginBottom: 6 }}>NAME</div>
                <input value={name} onChange={e => setName(e.target.value)} placeholder="Their name" style={inputStyle} />
              </div>

              {/* Birth date */}
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 10, color: 'var(--very-muted)', letterSpacing: 1, marginBottom: 6 }}>BIRTH DATE</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1.5fr', gap: 8 }}>
                  <input type="number" placeholder="Day" min="1" max="31" value={birthDay} onChange={e => setBirthDay(e.target.value)} style={inputStyle} />
                  <input type="number" placeholder="Month" min="1" max="12" value={birthMonth} onChange={e => setBirthMonth(e.target.value)} style={inputStyle} />
                  <input type="number" placeholder="Year" value={birthYear} onChange={e => setBirthYear(e.target.value)} style={inputStyle} />
                </div>
              </div>

              {/* Birth city */}
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 10, color: 'var(--very-muted)', letterSpacing: 1, marginBottom: 6 }}>BIRTH CITY</div>
                <LocationSearch
                  placeholder="City or town..."
                  initialValue={partner.birth_lat ? '' : ''}
                  onSelect={coords => setBirthLocation(coords)}
                />
              </div>

              {/* Birth time */}
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 10, color: 'var(--very-muted)', letterSpacing: 1, marginBottom: 6 }}>
                  BIRTH TIME <span style={{ textTransform: 'none', letterSpacing: 0, color: '#444' }}>— optional, improves accuracy</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  <input type="number" placeholder="Hour (0–23)" value={birthHour} onChange={e => setBirthHour(e.target.value)} style={inputStyle} />
                  <input type="number" placeholder="Min (0–59)" value={birthMin} onChange={e => setBirthMin(e.target.value)} style={inputStyle} />
                </div>
              </div>

              {/* What worked */}
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 10, color: 'var(--very-muted)', letterSpacing: 1, marginBottom: 6 }}>
                  WHAT WORKED <span style={{ textTransform: 'none', letterSpacing: 0, color: '#444' }}>— optional</span>
                </div>
                <textarea rows={3} value={whatWorked} onChange={e => setWhatWorked(e.target.value)}
                  placeholder="What felt good, aligned, or easy..." style={textareaStyle} />
              </div>

              {/* Challenges */}
              <div style={{ marginBottom: 18 }}>
                <div style={{ fontSize: 10, color: 'var(--very-muted)', letterSpacing: 1, marginBottom: 6 }}>
                  CHALLENGES <span style={{ textTransform: 'none', letterSpacing: 0, color: '#444' }}>— optional</span>
                </div>
                <textarea rows={3} value={challenges} onChange={e => setChallenges(e.target.value)}
                  placeholder="What was hard, misaligned, or recurring friction..." style={textareaStyle} />
              </div>

              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={cancelEdit}
                  style={{ padding: '9px 18px', background: 'none', border: '0.5px solid rgba(255,255,255,0.1)', color: 'var(--very-muted)', fontSize: 12, cursor: 'pointer' }}>
                  Cancel
                </button>
                <button onClick={handleSave} disabled={saving} style={{
                  flex: 1, padding: '9px 0',
                  background: 'rgba(201,168,76,0.1)', border: '0.5px solid rgba(201,168,76,0.35)',
                  color: 'var(--gold)', fontSize: 12, letterSpacing: 0.5,
                  cursor: saving ? 'default' : 'pointer',
                }}>
                  {saving ? 'Saving…' : 'Save changes'}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default function SavedPartners() {
  const navigate = useNavigate();
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!getToken()) { navigate('/login'); return; }
    api.get('/partner-tests')
      .then(data => { setPartners(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [navigate]);

  const handleDelete = (id) => setPartners(p => p.filter(x => x.id !== id));

  const handleUpdate = (id, updated) => {
    setPartners(p => p.map(x => x.id === id ? { ...x, ...updated } : x));
  };

  return (
    <div className="auth-screen" style={{ paddingTop: 60, paddingBottom: 80 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 8 }}>
        <button
          onClick={() => navigate('/settings')}
          style={{ background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: 14, padding: 0 }}
        >
          ← Back
        </button>
        <div className="display-sm">Uploaded partners</div>
      </div>

      <div style={{ fontSize: 12, color: 'var(--very-muted)', marginBottom: 32, lineHeight: 1.6 }}>
        Tap any entry to view or add relationship notes. Paula uses this context.
      </div>

      {loading && (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <span className="spinner" />
        </div>
      )}

      {!loading && partners.length === 0 && (
        <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--very-muted)', fontSize: 13 }}>
          <div style={{ marginBottom: 16 }}>No saved partners yet.</div>
          <button
            onClick={() => navigate('/partner-test')}
            style={{ background: 'none', border: '1px solid #2a2a2a', color: 'var(--muted)', fontSize: 13, padding: '10px 20px', cursor: 'pointer' }}
          >
            Test My Partner →
          </button>
        </div>
      )}

      {!loading && partners.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {partners.map(p => (
            <PartnerCard key={p.id} partner={p} onDelete={handleDelete} onUpdate={handleUpdate} />
          ))}
        </div>
      )}
    </div>
  );
}
